import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { useAuthStore } from './auth.store';

export type ThemeMode = 'light' | 'dark';

interface ThemeStoreState {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  toggle: () => void;
}

/**
 * Default theme when the persist store has no cached choice.
 * Operator opts into dark mode explicitly via the header toggle —
 * we do NOT follow `prefers-color-scheme` because most of our
 * tablet installs (POS, restaurant, KDS) run on OS dark mode by
 * default and the operator expects the light UI unless they say
 * otherwise.
 */
function getInitialTheme(): ThemeMode {
  return 'light';
}

/**
 * Resolve the persist bucket key for the CURRENT user, so two
 * operators sharing a tablet (POS terminal, restaurant floor, KDS
 * screen) each get their own theme choice. Falls back to `guest`
 * when there is no authenticated user yet (login screen, hard
 * refresh before hydration).
 *
 * We read the auth-store state directly instead of listening — the
 * key only needs to be right at read/write time, and the ThemeToggle
 * component already re-mounts under the AuthGuard so a user swap
 * repaints the store from the new bucket.
 */
function persistKeyForCurrentUser(): string {
  const userId = useAuthStore.getState().user?.id ?? 'guest';
  return `theme-store:${userId}`;
}

/**
 * Wrap `window.localStorage` so every read/write routes through the
 * per-user key. Zustand's `persist({ name })` treats the name as
 * static, so instead we keep `name` constant and rewrite the key at
 * the storage layer. This preserves standard `persist` rehydration
 * on mount + the per-user semantics.
 */
const userScopedStorage = createJSONStorage<Pick<ThemeStoreState, 'theme'>>(
  () => ({
    getItem: (_name) => {
      if (typeof window === 'undefined') return null;
      return window.localStorage.getItem(persistKeyForCurrentUser());
    },
    setItem: (_name, value) => {
      if (typeof window === 'undefined') return;
      window.localStorage.setItem(persistKeyForCurrentUser(), value);
    },
    removeItem: (_name) => {
      if (typeof window === 'undefined') return;
      window.localStorage.removeItem(persistKeyForCurrentUser());
    },
  })
);

export const useThemeStore = create<ThemeStoreState>()(
  devtools(
    persist(
      (set, get) => ({
        theme: getInitialTheme(),
        setTheme: (theme) =>
          set(() => ({ theme }), undefined, 'theme/setTheme'),
        toggle: () =>
          set(
            () => ({ theme: get().theme === 'dark' ? 'light' : 'dark' }),
            undefined,
            'theme/toggle'
          ),
      }),
      {
        name: 'theme-store',
        storage: userScopedStorage,
        partialize: (state) => ({ theme: state.theme }),
      }
    ),
    { name: 'theme-store' }
  )
);

/**
 * Rehydrate the theme store the moment the authenticated user id
 * changes — login, logout, impersonation swap, session restore.
 * `userScopedStorage` reads the CURRENT user's bucket, so calling
 * `persist.rehydrate()` here loads the new user's theme choice
 * (or the light-mode default when the bucket is empty). Without
 * this, the store would carry the previous user's theme until a
 * hard refresh.
 */
if (typeof window !== 'undefined') {
  let lastUserId = useAuthStore.getState().user?.id ?? null;
  useAuthStore.subscribe((state) => {
    const nextUserId = state.user?.id ?? null;
    if (nextUserId === lastUserId) return;
    lastUserId = nextUserId;
    void useThemeStore.persist.rehydrate();
  });
}
